import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
// Importiamo Slider, Typography, Box e CircularProgress da Material UI
import { Link, Slider, Typography, Box, CircularProgress } from "@mui/material";
import HTMLFlipBook from "react-pageflip";

// Assicurati che questi import puntino ai percorsi corretti nel tuo progetto
import ProductgridList from "./ProductgridList";
import ProductMobile from "../../pages/shop-product/ProductMobile";
import ProductPageCatalog from "../../pages/shop-product/ProductPageCatalog";
import ProductPageCatalogTemplate from "../../pages/shop-product/ProductPageCatalogTemplate";

const ShopProductsFlip = (props) => {
  const {
    products,
    layout,
    isLoggedUser,
    visualizzaPrezzi,
    oneProduct,
    catalogo,
  } = props;

  const flipBook = useRef(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totPages, setTotPages] = useState(0);

  // STATI AGGIUNTI PER IL CARICAMENTO
  const [isBookReady, setIsBookReady] = useState(false);
  const [mountBook, setMountBook] = useState(false);

  // Stato per gestire il dialog di modifica quantità
  const [editDialog, setEditDialog] = useState({
    isOpen: false,
    id: null,
    currentQty: 0,
    min: 0,
    conf: 1,
  });

  const RENDER_BUFFER = 6;

  // Funzione per calcolare il numero di varianti
  const getNumOfVariants = useCallback((product, template) => {
    if (!product || !product.variation) return 0;

    if (template === "grid_with_sizes") {
      const groupedVariants = product.variation.map((v, indexColor) => {
        return {
          colorId: `c-${indexColor}`,
          color: v.color || `Variante ${indexColor + 1}`,
          sizes: (v.size && v.size.length > 0) ? v.size.map((sizeItem, indexSize) => {
            return {
              id: `${indexColor}-${indexSize}`,
              sizeLabel: sizeItem.name || sizeItem.label || "Unica",
              quantity: 0,
              price: sizeItem["pricesco"] || sizeItem.pricesco || "0.00",
              src: global.baseUrlImage + sizeItem.nomImg + "?t=" + Math.random(),
              nomImg: sizeItem.skuSizeColor,
            };
          }) : []
        };
      });
      return groupedVariants.length;
    }

    const initialVariants = product?.variation?.flatMap((v, indexColor) => {
      if (!v || !v.size || v.size.length === 0) return [];
      return v.size.map((sizeItem, indexSize) => {
        return {
          id: `${indexColor}-${indexSize}`,
          color: v.colore || `Variante ${indexColor + 1}`,
          quantity: 0,
          price: sizeItem["pricesco"] || sizeItem.pricesco || "0.00",
          src: global.baseUrlImage + sizeItem.nomImg + "?t=" + Math.random(),
          nomImg: sizeItem.skuSizeColor,
        };
      });
    });
    return initialVariants.length;
  }, []);

  // 1. PRE-CALCOLO DELLE PAGINE
  const pagesArray = useMemo(() => {
    let globalIndex = 0;
    const result = [];

    (catalogo || []).forEach((pagina, outerIndex) => {
      if (
        (pagina.template === "grid" || pagina.template === "grid_with_sizes") &&
        (!pagina.IDArtBas || pagina.IDArtBas === 0)
      ) {
        return;
      }

      if (pagina.template === "end") {
        result.push({
          isEnd: true,
          key: `end-${outerIndex}`,
          index: globalIndex++,
        });
        return;
      }

      const product = products.find((p) => p.id === pagina.IDArtBas);
      const loopCount =
        pagina.numPagine &&
          pagina.numPagine > 1 &&
          (pagina.template === "grid" || pagina.template === "grid_with_sizes")
          ? pagina.numPagine
          : 1;

      const numVariants = getNumOfVariants(product, pagina.template);
      const offsetPerPage =
        pagina.numPagine && pagina.numPagine > 1
          ? Math.ceil(numVariants / pagina.numPagine)
          : numVariants;

      for (let innerIndex = 0; innerIndex < loopCount; innerIndex++) {
        const offset = innerIndex * offsetPerPage;
        result.push({
          isEnd: false,
          key: `page-${outerIndex}-${innerIndex}`,
          pagina: { ...pagina, ord: pagina.ord + innerIndex },
          product,
          offsetPerPage,
          offset,
          index: globalIndex++,
        });
      }
    });

    return result;
  }, [catalogo, products, getNumOfVariants]);

  // Gestione cambio pagina dal FlipBook
  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
    if (flipBook.current && flipBook.current.pageFlip()) {
      const count = flipBook.current.pageFlip().getPageCount();
      setTotPages(count > 0 ? count - 1 : 0);
    }
  }, []);

  // GESTIONE TASTIERA
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!flipBook.current || !flipBook.current.pageFlip()) return;

      if (event.key === "ArrowRight") {
        flipBook.current.pageFlip().flipNext();
      } else if (event.key === "ArrowLeft") {
        flipBook.current.pageFlip().flipPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // NUOVO EFFECT: Risolve il problema del Thread Bloccato.
  // Diamo al browser 300 millisecondi esatti per poter "disegnare" (paint) 
  // il loader sullo schermo prima di iniziare l'operazione pesante.
  useEffect(() => {
    setMountBook(false);
    setIsBookReady(false);

    const timer = setTimeout(() => {
      setMountBook(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [catalogo]); // Riavvia il timer se cambia il catalogo

  // Controlla quando il FlipBook ha finito di montare le pagine
  useEffect(() => {
    if (!mountBook) return;

    const timer = setTimeout(() => {
      if (flipBook.current && flipBook.current.pageFlip()) {
        const count = flipBook.current.pageFlip().getPageCount();
        setTotPages(count > 0 ? count - 1 : 0);
        setIsBookReady(true); // Libro pronto, togliamo il loader!
      } else if (pagesArray.length === 0) {
        setIsBookReady(true);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [catalogo, pagesArray, mountBook]);

  // GESTIONE SLIDER
  const handleSliderChange = (event, newValue) => {
    if (flipBook.current && flipBook.current.pageFlip()) {
      flipBook.current.pageFlip().flip(newValue);
    }
  };

  const handleDialogSave = () => {
    setEditDialog({ ...editDialog, isOpen: false });
  };

  return (
    <div
      className="shop-bottom-area mt-12"
      id="products-area"
      style={{
        // Aggiunto position relative per gestire il loader in absolute
        position: "relative",
        minHeight: "800px", // Mantiene lo spazio vuoto in attesa del rendering
        ...(oneProduct
          ? {}
          : global.aziendaID === 4
            ? {
              overflowY: "scroll",
              maxWidth: "85vw",
              overflowX: "hidden",
              scrollbarWidth: "none",
            }
            : {
              overflowY: "scroll",
              maxHeight: "800px",
              maxWidth: "85vw",
              overflowX: "hidden",
              scrollbarWidth: "none",
            })
      }}
    >
      {/* --- MODALE EDIT QUANTITÀ --- */}
      {editDialog.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setEditDialog({ ...editDialog, isOpen: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              minWidth: "300px",
              textAlign: "center",
            }}
          >
            <h4>Modifica Quantità</h4>
            <input
              type="number"
              autoFocus
              value={editDialog.currentQty}
              onChange={(e) =>
                setEditDialog({ ...editDialog, currentQty: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleDialogSave();
              }}
              style={{
                fontSize: "2rem",
                padding: "10px",
                textAlign: "center",
                border: "2px solid #ddd",
                borderRadius: "8px",
                width: "100%",
              }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "10px" }}>
              <button
                onClick={() => setEditDialog({ ...editDialog, isOpen: false })}
                className="btn btn-secondary"
                style={{ padding: "10px 20px", border: "1px solid #ccc", background: "#f0f0f0", borderRadius: "5px" }}
              >
                Annulla
              </button>
              <button
                onClick={handleDialogSave}
                className="btn btn-primary"
                style={{ padding: "10px 20px", background: "#007bff", color: "white", border: "none", borderRadius: "5px" }}
              >
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- BLOCCO DI CARICAMENTO --- */}
      {/* Ora è in position absolute e con uno zIndex alto: si piazza sopra a tutto finché non scompare */}
      {!isBookReady && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingTop: "20vh", // Lo abbassiamo visivamente un po'
            backgroundColor: "#ffffff", // Copre il rendering sottostante
            zIndex: 10,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3, color: "#555" }}>
            Preparazione del catalogo...
          </Typography>
        </Box>
      )}

      {/* --- CONTENITORE DEL FLIPBOOK --- */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          // Nascondiamo il div senza usare display: none (che romperebbe react-pageflip)
          opacity: isBookReady ? 1 : 0,
          pointerEvents: isBookReady ? "auto" : "none",
          transition: "opacity 0.3s ease-in-out", // Aggiunto un leggero fade per renderlo elegante
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
          {/* Bottone Inizio */}
          {currentPage > 0 && (
            <div className="shopping-cart-btn btn-hover text-center" style={{ marginRight: "10px" }}>
              <Link
                className="default-btn"
                component="button"
                onClick={() => flipBook.current.pageFlip().flip(0)}
                underline="none"
                style={{ height: "65vh", alignContent: "center" }}
              >
                &lt;&lt;
              </Link>
            </div>
          )}
          {/* Bottone Indietro */}
          {currentPage > 0 && (
            <div className="shopping-cart-btn btn-hover text-center" style={{ marginRight: "10px" }}>
              <Link
                className="default-btn"
                component="button"
                onClick={() => flipBook.current.pageFlip().flipPrev()}
                underline="none"
                style={{ height: "65vh", alignContent: "center" }}
              >
                &lt;
              </Link>
            </div>
          )}

          {/* RENDERING EFFETTIVO DEL FLIPBOOK (Avviene solo dopo il mountBook triggerato dal timeout) */}
          {mountBook && (
            <HTMLFlipBook
              size="fixed"
              width={600}
              height={800}
              minWidth={315}
              maxWidth={1000}
              minHeight={600}
              maxHeight={1000}
              maxShadowOpacity={1}
              showCover={false}
              mobileScrollSupport={true}
              className="demo-book"
              onFlip={onFlip}
              ref={flipBook}
            >
              {pagesArray.map((pageConf) => {
                const isVisible = Math.abs(pageConf.index - currentPage) <= RENDER_BUFFER;

                return (
                  <div
                    key={pageConf.key}
                    className="demoPage"
                    style={{
                      backgroundColor: "#fff",
                      padding: "20px",
                      minHeight: "600px",
                    }}
                  >
                    {isVisible ? (
                      pageConf.isEnd ? (
                        <div
                          style={{
                            textAlign: "center",
                            height: "100%",
                            justifyContent: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={global.placeholderImage}
                            alt="Logo"
                            style={{ maxWidth: "200px", marginBottom: "20px" }}
                          />
                          <h2>Fine del Catalogo</h2>
                          <p className="mb-4">Scopri la nostra collezione tessile</p>
                          <div className="shopping-cart-btn btn-hover text-center">
                            <a
                              href="/shop-grid-full-width/show=GRAFICHE"
                              className="default-btn"
                              style={{ display: "inline-block" }}
                            >
                              Vai al Tessile
                            </a>
                          </div>
                        </div>
                      ) : pageConf.product ? (
                        <ProductPageCatalogTemplate
                          isLoggedUser={isLoggedUser}
                          visualizzaPrezzi={visualizzaPrezzi}
                          pagina={pageConf.pagina}
                          product={pageConf.product}
                          notShowGridAvailability={true}
                          showCatalogo={true}
                          catalogoTemplate={true}
                          offsetPerPage={pageConf.offsetPerPage}
                          variantOffset={pageConf.offset}
                          setEditDialog={setEditDialog}
                          editDialog={editDialog}
                        />
                      ) : null
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#eee",
                        }}
                      ></div>
                    )}
                  </div>
                );
              })}
            </HTMLFlipBook>
          )}

          {/* Bottone Avanti */}
          {currentPage < totPages && (
            <div className="shopping-cart-btn btn-hover text-center" style={{ marginLeft: "10px" }}>
              <Link
                className="default-btn"
                component="button"
                onClick={() => flipBook.current.pageFlip().flipNext()}
                underline="none"
                style={{ height: "65vh", alignContent: "center" }}
              >
                &gt;
              </Link>
            </div>
          )}
          {/* Bottone Fine */}
          {currentPage < totPages && (
            <div className="shopping-cart-btn btn-hover text-center" style={{ marginLeft: "10px" }}>
              <Link
                className="default-btn"
                component="button"
                onClick={() => flipBook.current.pageFlip().flip(totPages)}
                underline="none"
                style={{ height: "65vh", alignContent: "center" }}
              >
                &gt;&gt;
              </Link>
            </div>
          )}
        </div>

        {/* --- CONTROLLI SLIDER E PAGINAZIONE --- */}
        <Box sx={{ width: 300, mt: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="caption" display="block" gutterBottom>
            Pagina {currentPage + 1} di {totPages + 1}
          </Typography>

          <Slider
            size="small"
            value={currentPage}
            min={0}
            max={totPages}
            onChange={handleSliderChange}
            aria-label="Pagine Catalogo"
            valueLabelDisplay="auto"
            valueLabelFormat={(x) => x + 1}
            sx={{
              color: "primary.main",
            }}
          />

          <Typography variant="caption" sx={{ color: "#999", mt: 1 }}>
            Usa lo slider o le frecce della tastiera per navigare
          </Typography>
        </Box>
      </div>
    </div>
  );
};

ShopProductsFlip.propTypes = {
  layout: PropTypes.string,
  catalogo: PropTypes.array,
};

export default ShopProductsFlip;